/**
 * Knowledge API (contract: being built in parallel; see contracts.ts).
 */

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/vue-query";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { http } from "./client";
import type {
  AskConfig,
  AskResponse,
  ChangeSet,
  Connector,
  Entity,
  EntityPage,
  EntityRevision,
  GraphResponse,
  InboxItem,
  InboxSummary,
  Items,
  KnowledgeAsset,
  KnowledgeDocument,
  KnowledgeHealth,
  KnowledgeOverview,
  KnowledgeSchema,
  KnowledgeSource,
  KnowledgeUploadResponse,
  LineageOverview,
  MaintenanceFinding,
  MaintenanceKind,
  MaintenancePlan,
  MaintenanceSummary,
  Mention,
  ResolvedRef,
  SearchResponse,
  SourceRun,
} from "./contracts";

const base = "/api/v2/knowledge";
const enc = encodeURIComponent;
const entityPath = (type: string, slug: string) => `${base}/entities/${enc(type)}/${enc(slug)}`;

// ---------------------------------------------------------------------------
// Search

export interface SearchParams {
  q: string;
  types?: string[];
  lineage?: string;
  tags?: string[];
  /** Source ids. */
  sources?: string[];
  /** Entity statuses (default: active). */
  status?: string[];
  limit?: number;
  offset?: number;
  /** Facet counts over the whole result set (+ "did you mean" when nothing matched). */
  facets?: boolean;
  /** Search-as-you-type: kept out of the zero-result statistics. */
  instant?: boolean;
}

const list = (v?: string[]) => (v?.length ? v : undefined);

export function useKnowledgeSearch(params: MaybeRefOrGetter<SearchParams>, enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: ["knowledge", "search", computed(() => toValue(params))],
    // A query, or a filter alone (browse all systems, everything tagged "ops", …).
    enabled: computed(() => {
      const p = toValue(params);
      return toValue(enabled) && (p.q.trim().length > 0 || Boolean(p.types?.length || p.tags?.length || p.lineage || p.sources?.length));
    }),
    queryFn: () => {
      const p = toValue(params);
      return http.get<SearchResponse>(`${base}/search`, {
        q: p.q.trim(),
        types: list(p.types),
        lineage: p.lineage || undefined,
        tags: list(p.tags),
        sources: list(p.sources),
        status: list(p.status),
        limit: p.limit ?? 20,
        offset: p.offset ?? 0,
        facets: p.facets ? "true" : undefined,
        instant: p.instant ? "true" : undefined,
      });
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Landing aggregates: counts per type and lineage, tags, recent changes, mine, attention. */
export function useKnowledgeOverview() {
  return useQuery({ queryKey: ["knowledge", "overview"], queryFn: () => http.get<KnowledgeOverview>(`${base}/overview`), staleTime: 60_000 });
}

export function useLineageOverview(slug: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: ["knowledge", "lineage", computed(() => toValue(slug))],
    queryFn: () => http.get<LineageOverview>(`${base}/lineages/${enc(toValue(slug))}`),
    staleTime: 60_000,
  });
}

/** Resolve an external or v1 reference (e.g. gvk:kg://global/System/checkout) to a page. */
export function useResolveRef(ref: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: ["knowledge", "resolve", computed(() => toValue(ref))],
    enabled: computed(() => toValue(enabled) && toValue(ref).trim().length >= 3),
    queryFn: () => http.get<ResolvedRef>(`${base}/resolve`, { ref: toValue(ref).trim() }),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Entities

export function useEntity(type: MaybeRefOrGetter<string>, slug: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: ["knowledge", "entity", computed(() => toValue(type)), computed(() => toValue(slug))],
    queryFn: () => http.get<EntityPage>(entityPath(toValue(type), toValue(slug))),
  });
}

export function useEntityHistory(type: MaybeRefOrGetter<string>, slug: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: ["knowledge", "history", computed(() => toValue(type)), computed(() => toValue(slug))],
    enabled: computed(() => toValue(enabled)),
    queryFn: async () => (await http.get<{ revisions: EntityRevision[] }>(`${entityPath(toValue(type), toValue(slug))}/history`)).revisions,
  });
}

export interface EntityInput {
  type: string;
  name: string;
  subtype?: string;
  aliases?: string[];
  tags?: string[];
  bodyMd?: string;
  props?: Record<string, unknown>;
  reason: string;
  /** Create even when the service reports a likely duplicate (409 code "duplicate"). */
  allowDuplicate?: boolean;
}

export const createEntity = (input: EntityInput) => http.post<{ entity: Entity }>(`${base}/entities`, input);

export interface EntityUpdate {
  name?: string;
  aliases?: string[];
  tags?: string[];
  bodyMd?: string;
  props?: Record<string, unknown>;
  baseRevision: number;
  reason: string;
}

export const updateEntity = (type: string, slug: string, input: EntityUpdate) => http.put<{ entity: Entity }>(entityPath(type, slug), input);
export const archiveEntity = (type: string, slug: string, reason: string) => http.del(entityPath(type, slug), { reason });
export const restoreEntity = (type: string, slug: string, revision: number, reason: string) =>
  http.post<{ entity?: Entity }>(`${entityPath(type, slug)}/restore`, { revision, reason });

// ---------------------------------------------------------------------------
// Change log

export interface ChangeFilter {
  actor?: string;
  source?: string;
  type?: string;
  /** Change sets touching entities of this lineage. */
  lineage?: string;
  before?: string;
  limit?: number;
}

export function useKnowledgeChanges(filter: MaybeRefOrGetter<ChangeFilter>) {
  return useQuery({
    queryKey: ["knowledge", "changes", computed(() => toValue(filter))],
    queryFn: async () => (await http.get<Items<ChangeSet>>(`${base}/changes`, { limit: 50, ...toValue(filter) })).items,
    placeholderData: keepPreviousData,
  });
}

export const revertChangeSet = (id: string, reason: string, force = false) =>
  http.post<{ changeSetId: string; conflicts: unknown[] }>(`${base}/change-sets/${enc(id)}/revert`, { reason, force });

// ---------------------------------------------------------------------------
// Graph views

export type GraphView = "neighborhood" | "topology" | "impact" | "path";

export function useGraph(view: MaybeRefOrGetter<GraphView>, params: MaybeRefOrGetter<Record<string, string | number | undefined>>, enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: ["knowledge", "graph", computed(() => toValue(view)), computed(() => toValue(params))],
    enabled: computed(() => toValue(enabled)),
    queryFn: () => http.get<GraphResponse>(`${base}/graph/${toValue(view)}`, toValue(params)),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Documents

export function useDocument(id: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: ["knowledge", "document", computed(() => toValue(id))],
    queryFn: () => http.get<{ document: KnowledgeDocument; mentions: Mention[] | { type: string; slug: string; name: string; uri: string }[] }>(`${base}/documents/${enc(toValue(id))}`),
  });
}

/** Largest knowledge upload the server accepts. */
export const MAX_UPLOAD_BYTES = 300 * 1024 * 1024;

/** File types the upload accepts (documents and images). */
export const UPLOAD_ACCEPT = ".pdf,.md,.markdown,.txt,.html,.htm,.csv,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff";

export const uploadDocument = (file: File) => {
  const form = new FormData();
  form.append("file", file);
  // ≤ 20 MB: converted at once (documentId; captions/OCR follow in enrichJobId); larger (≤ 300 MB): a knowledge-upload job (jobId).
  return http.upload<KnowledgeUploadResponse>(`${base}/uploads`, form);
};

/** Images of a document (pictures, scanned pages) with their captions and waiting AI work. */
export function useDocumentAssets(id: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: ["knowledge", "document", computed(() => toValue(id)), "assets"],
    queryFn: () => http.get<{ items: KnowledgeAsset[]; pending: number }>(`${base}/documents/${enc(toValue(id))}/assets`),
  });
}

/** Caption the images / read the scans of a document now (waiting and failed ones). */
export const enrichDocument = (id: string) => http.post<{ jobId: string }>(`${base}/documents/${enc(id)}/enrich`, {});

// ---------------------------------------------------------------------------
// Sources

export function useSources() {
  return useQuery({ queryKey: ["knowledge", "sources"], queryFn: async () => (await http.get<Items<KnowledgeSource>>(`${base}/sources`)).items });
}

export function useSourceRuns(id: MaybeRefOrGetter<string | null>) {
  return useQuery({
    queryKey: ["knowledge", "sources", "runs", computed(() => toValue(id))],
    enabled: computed(() => Boolean(toValue(id))),
    queryFn: async () => (await http.get<Items<SourceRun>>(`${base}/sources/${enc(toValue(id)!)}/runs`)).items,
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === "running" || r.status === "queued") ? 3_000 : false),
  });
}

export function useConnectors() {
  return useQuery({ queryKey: ["knowledge", "connectors"], queryFn: async () => (await http.get<Items<Connector>>(`${base}/connectors`)).items, staleTime: 5 * 60_000 });
}

export interface SourceInput {
  type: string;
  name: string;
  config: Record<string, unknown>;
  schedule?: string;
  /** Keycloak groups/roles that may read synced content; empty = everyone with knowledge.read. */
  visibility?: string[];
  extract?: boolean;
  /** AI suggestions skip documents whose title, path or labels contain one of these words. */
  extractSkip?: string[];
  /** Multiplies the search ranking of the source's documents; null = the default for its kind. */
  searchWeight?: number | null;
}

export const createSource = (body: SourceInput) => http.post<KnowledgeSource>(`${base}/sources`, body);
export const updateSource = (id: string, body: Partial<Pick<KnowledgeSource, "name" | "config" | "schedule" | "enabled" | "visibility" | "extract" | "extractSkip" | "searchWeight">>) =>
  http.patch<KnowledgeSource>(`${base}/sources/${enc(id)}`, body);
export const deleteSource = (id: string) => http.del(`${base}/sources/${enc(id)}`);
export const syncSource = (id: string, mode: "incremental" | "full" = "incremental") => http.post<{ jobId: string }>(`${base}/sources/${enc(id)}/sync`, { mode });
export const reextractSource = (id: string) => http.post<{ queued: number }>(`${base}/sources/${enc(id)}/reextract`);

// ---------------------------------------------------------------------------
// Inbox, health, schema, ask

export interface InboxFilter {
  kind?: "proposal" | "conflict" | "duplicate" | "feedback";
  origin?: string;
  sourceId?: string;
  entityType?: string;
  minSupport?: number;
  maxSupport?: number;
  q?: string;
  /** The pre-triage verdict (src/knowledge/triage.ts); untriaged = no verdict yet. */
  verdict?: "accept" | "reject" | "review" | "untriaged";
  /** A triage category, e.g. "generic name" (the groups view). */
  category?: string;
  /** One group of the groups view (GET /knowledge/inbox/groups → key). */
  groupKey?: string;
}

const inboxQuery = (filter: InboxFilter, extra: Record<string, string | number | undefined> = {}) => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...filter, ...extra })) if (v !== undefined && v !== "") params.set(k, String(v));
  const q = params.toString();
  return q ? `?${q}` : "";
};

/** Open inbox items matching the filter, newest first, 50 per page. */
export function useInbox(filter: MaybeRefOrGetter<InboxFilter> = {}) {
  return useInfiniteQuery({
    queryKey: ["knowledge", "inbox", "items", computed(() => ({ ...toValue(filter) }))],
    queryFn: ({ pageParam }) => http.get<{ items: InboxItem[]; nextCursor: string | null }>(`${base}/inbox${inboxQuery(toValue(filter), { limit: 50, cursor: pageParam })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

/** Counts per kind, source, entity type and support for the current filter. */
export function useInboxSummary(filter: MaybeRefOrGetter<InboxFilter> = {}, enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: ["knowledge", "inbox", "summary", computed(() => ({ ...toValue(filter) }))],
    enabled: computed(() => toValue(enabled)),
    queryFn: () => http.get<InboxSummary>(`${base}/inbox/summary${inboxQuery(toValue(filter))}`),
    placeholderData: keepPreviousData,
  });
}

/** Accept or reject everything the filter matches (runs as a job). */
export const bulkInbox = (body: { decision: "accept" | "reject"; filter: InboxFilter; reason?: string; remember?: boolean }) =>
  http.post<{ jobId: string | null; matched: number }>(`${base}/inbox/bulk`, body);

export const acceptInboxItem = (id: string) => http.post(`${base}/inbox/${enc(id)}/accept`);
export const rejectInboxItem = (id: string, reason?: string) => http.post(`${base}/inbox/${enc(id)}/reject`, { reason });

export function useKnowledgeHealth() {
  return useQuery({ queryKey: ["knowledge", "health"], queryFn: () => http.get<KnowledgeHealth>(`${base}/health`), staleTime: 60_000 });
}

export function useKnowledgeSchema() {
  return useQuery({ queryKey: ["knowledge", "schema"], queryFn: () => http.get<KnowledgeSchema>(`${base}/schema`), staleTime: 10 * 60_000 });
}

export interface AskRequest {
  question: string;
  lineages?: string[];
  types?: string[];
  sources?: string[];
  /** Search hits the answer is based on (1–20). */
  limit?: number;
  /** Earlier turns of this conversation, oldest first (follow-ups). */
  history?: { question: string; answer: string }[];
}

export const ask = (body: AskRequest) => http.post<AskResponse>(`${base}/ask`, body);

/** Knowledge sources the viewer can see (for Ask's source filter): the source facet over all documents. */
export function useVisibleSources() {
  return useQuery({
    queryKey: ["knowledge", "visible-sources"],
    queryFn: async () => (await http.get<SearchResponse>(`${base}/search`, { q: "", types: "document", limit: 1, facets: "true", instant: "true" })).facets?.sources ?? [],
    staleTime: 5 * 60_000,
  });
}

export function useAskConfig() {
  return useQuery({ queryKey: ["knowledge", "ask", "config"], queryFn: () => http.get<AskConfig>(`${base}/ask/config`), staleTime: 5 * 60_000 });
}

// ---------------------------------------------------------------------------
// Maintenance (knowledge.admin)

export interface MaintenanceFilter {
  kind?: MaintenanceKind;
  status?: MaintenanceFinding["status"];
  q?: string;
}

export function useMaintenanceSummary(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: ["knowledge", "maintenance", "summary"],
    enabled: computed(() => toValue(enabled)),
    queryFn: () => http.get<MaintenanceSummary>(`${base}/maintenance/summary`),
    refetchInterval: (q) => (q.state.data?.running ? 4_000 : false),
  });
}

/** Findings for the filter, most confident first, 50 per page. */
export function useMaintenanceFindings(filter: MaybeRefOrGetter<MaintenanceFilter>) {
  return useInfiniteQuery({
    queryKey: ["knowledge", "maintenance", "findings", computed(() => ({ ...toValue(filter) }))],
    queryFn: ({ pageParam }) => {
      const f = toValue(filter);
      return http.get<{ items: MaintenanceFinding[]; nextCursor: string | null }>(`${base}/maintenance/findings`, {
        kind: f.kind, status: f.status, q: f.q?.trim() || undefined, limit: 50, cursor: pageParam,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export const startMaintenanceScan = () => http.post<{ scanId: string; jobId: string; started: boolean }>(`${base}/maintenance/scan`);
export const dismissFindings = (ids: string[], reason?: string) => http.post<{ dismissed: number }>(`${base}/maintenance/dismiss`, { ids, reason });
export const reopenFindings = (ids: string[]) => http.post<{ reopened: number }>(`${base}/maintenance/reopen`, { ids });
/** Dry run: what applying these findings would change (and the token to apply exactly that). */
export const planFindings = (ids: string[], choices: Record<string, { into?: string }>, reason?: string) =>
  http.post<MaintenancePlan>(`${base}/maintenance/plan`, { ids, choices, reason });
export const applyFindings = (ids: string[], choices: Record<string, { into?: string }>, token: string, reason?: string) =>
  http.post<{ jobId: string }>(`${base}/maintenance/apply`, { ids, choices, token, reason });

// ---------------------------------------------------------------------------
// URIs

/** knowledge://entity/<type>/<slug> → /k/<type>/<slug>; knowledge://doc/<id> → /k/doc/<id>. */
export function uriToPath(uri: string): string | null {
  const entity = /^knowledge:\/\/entity\/([^/]+)\/(.+)$/.exec(uri);
  if (entity) return `/k/${entity[1]}/${entity[2]}`;
  const doc = /^knowledge:\/\/doc\/(.+)$/.exec(uri);
  if (doc) return `/k/doc/${doc[1]}`;
  return null;
}

export const entityHref = (ref: { type: string; slug: string }) => `/k/${enc(ref.type)}/${enc(ref.slug)}`;
